import { useMutation } from "@apollo/client";
import { Add, Check, Delete, Edit } from "@mui/icons-material";
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from "@mui/material";
import { t } from "i18next";
import React, { useState } from "react";
import { deleteVip, upsertVip } from "../../api/getVips";
import { showErrorMessage } from "../../helper/helper";
import { VipEntry } from "../../models/VipEntry";
import { colorPrimary } from "../../rsc/colors/colors";
import { AddVipDialog } from "./AddVipDialog";

type Props = {
    entries: VipEntry[]
    buyerId: number
    refetch: () => void
}

export const MillVipTable: React.FC<Props> = ({ entries, buyerId, refetch }) => {

    const [addOpen, setAddOpen] = useState(false)
    const [editingId, setEditingId] = useState<number | null>(null)
    const [editBoost, setEditBoost] = useState<number>(0)
    const [vipToDelete, setVipToDelete] = useState<VipEntry | null>(null)

    const [upsert, { loading: upsertLoading }] = useMutation(upsertVip, {
        onError: (e) => showErrorMessage(e),
    })

    const [remove, { loading: deleteLoading }] = useMutation(deleteVip, {
        onError: (e) => showErrorMessage(e),
    })

    const handleAdd = async (transactorId: number) => {
        await upsert({
            variables: {
                input: {
                    buyer_id: buyerId,
                    transactor_id: transactorId,
                    niaga_delta_boost: 0,
                    is_mill_vip: true,
                },
            },
        })
        setAddOpen(false)
        refetch()
    }

    const handleEdit = (vip: VipEntry) => {
        setEditingId(vip.id)
        setEditBoost(vip.niaga_delta_boost || 0)
    }

    const handleSave = async (vip: VipEntry) => {
        await upsert({
            variables: {
                input: {
                    buyer_id: vip.buyer_id,
                    transactor_id: vip.transactor_id,
                    niaga_delta_boost: editBoost,
                    is_mill_vip: vip.is_mill_vip,
                },
            },
        })
        setEditingId(null)
        refetch()
    }

    const handleDelete = async () => {
        if (!vipToDelete) return
        await remove({ variables: { id: vipToDelete.id } })
        setVipToDelete(null)
        refetch()
    }

    return (
        <Paper sx={{ p: 2 }}>
            <div className="pempem-flex-row" style={{ justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <Typography variant="h6">{t('mill_vip')}</Typography>
                <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={() => setAddOpen(true)}
                    sx={{ background: colorPrimary(), color: '#fff' }}>
                    {t('add')}
                </Button>
            </div>

            <TableContainer>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>{t('id')}</TableCell>
                            <TableCell>{t('name')}</TableCell>
                            <TableCell>{t('delta_boost')}</TableCell>
                            <TableCell align="right">{t('actions')}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {entries.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={4} align="center">
                                    <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                                        {t('no_vips_configured')}
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        )}
                        {entries.map((vip) => {
                            const isEditing = editingId === vip.id
                            return (
                                <TableRow key={vip.id} hover>
                                    <TableCell>{vip.transactor_id}</TableCell>
                                    <TableCell>{vip.person?.getFullName() ?? '—'}</TableCell>
                                    <TableCell>
                                        {isEditing ? (
                                            <TextField
                                                size="small"
                                                type="number"
                                                value={editBoost}
                                                onChange={(e) => setEditBoost(parseInt(e.target.value))}
                                                sx={{ maxWidth: 110 }} />
                                        ) : (
                                            vip.niaga_delta_boost ?? '–'
                                        )}
                                    </TableCell>
                                    <TableCell align="right">
                                        {isEditing ? (
                                            <IconButton
                                                size="small"
                                                color="primary"
                                                disabled={upsertLoading}
                                                onClick={() => handleSave(vip)}>
                                                <Check />
                                            </IconButton>
                                        ) : (
                                            <IconButton size="small" onClick={() => handleEdit(vip)}>
                                                <Edit fontSize="small" />
                                            </IconButton>
                                        )}
                                        <IconButton
                                            size="small"
                                            color="error"
                                            onClick={() => setVipToDelete(vip)}>
                                            <Delete fontSize="small" />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            )
                        })}
                    </TableBody>
                </Table>
            </TableContainer>

            <AddVipDialog
                open={addOpen}
                onClose={() => setAddOpen(false)}
                onConfirm={handleAdd}
                loading={upsertLoading} />

            <Dialog open={!!vipToDelete} onClose={() => setVipToDelete(null)} maxWidth="xs" fullWidth>
                <DialogTitle>{t('confirm_delete')}</DialogTitle>
                <DialogContent>
                    <DialogContentText>{t('confirm_delete_vip')}</DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setVipToDelete(null)} color="inherit">{t('cancel')}</Button>
                    <Button color="error" variant="contained" disabled={deleteLoading} onClick={handleDelete}>
                        {t('delete')}
                    </Button>
                </DialogActions>
            </Dialog>
        </Paper>
    )
}

export default MillVipTable;
